/**
 * The single API client.
 *
 * Two transports, chosen once at construction:
 *
 * - **web** keeps the refresh token in an httpOnly cookie the browser sends for
 *   us. JavaScript never sees it, so XSS cannot steal it. Requests go to the same
 *   origin (dev proxy, or a CDN in production) because the cookie is
 *   `sameSite: 'strict'`.
 * - **mobile** runs from `capacitor://localhost`, where cookies are unreliable, so
 *   the refresh token comes back in the body and lives in the OS keychain. The
 *   `X-Client: mobile` header selects this on the server.
 *
 * The access token is held in memory in both cases — never in `localStorage`,
 * which is readable by any injected script.
 */

export interface ApiErrorBody {
  error: { code: string; message: string; details?: unknown };
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** True when the server is telling us to back off and retry, not to give up. */
  get isTransient(): boolean {
    return this.status === 503 || this.status === 429;
  }
}

export interface TokenStore {
  getAccess(): string | null;
  setAccess(token: string | null): void;
  /** Mobile only. The web transport keeps the refresh token in an httpOnly cookie. */
  getRefresh(): string | null;
  setRefresh(token: string | null): void;
  /**
   * "This browser probably has a live refresh cookie."
   *
   * Not a credential — a boolean. The web transport cannot read its own httpOnly
   * cookie, so without this hint every anonymous visitor who touches a protected
   * route would 401, trigger a pointless refresh, and be told their session had
   * expired. It survives reload, which is also how the app knows to restore a
   * session on boot.
   */
  hasSessionHint(): boolean;
  setSessionHint(value: boolean): void;
  clear(): void;
}

const HINT_KEY = 'sag.session';

/** The web store: access token in memory, refresh token owned by the browser. */
export function memoryTokenStore(): TokenStore {
  let access: string | null = null;
  let hint = false;

  // `localStorage` is absent in unit tests and in an SSR pass; degrade to memory.
  const storage: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> | null =
    typeof localStorage === 'undefined' ? null : localStorage;

  return {
    getAccess: () => access,
    setAccess: (t) => void (access = t),
    getRefresh: () => null,
    setRefresh: () => undefined,
    hasSessionHint: () => (storage ? storage.getItem(HINT_KEY) === '1' : hint),
    setSessionHint: (value) => {
      hint = value;
      if (!storage) return;
      if (value) storage.setItem(HINT_KEY, '1');
      else storage.removeItem(HINT_KEY);
    },
    clear() {
      access = null;
      this.setSessionHint(false);
    },
  };
}

export type Transport = 'web' | 'mobile';

export interface SessionResponse {
  accessToken: string;
  refreshToken?: string;
  user: unknown;
}

export interface ApiClientOptions {
  /** Empty string means same-origin: `/api/v1/...`. Capacitor passes an absolute origin. */
  baseUrl?: string;
  transport?: Transport;
  store?: TokenStore;
  /** Called once when the session is unrecoverable. The router sends the user to /login. */
  onSessionExpired?: () => void;
  fetch?: typeof globalThis.fetch;
}

export interface RequestOptions {
  method?: string;
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined | null>;
  signal?: AbortSignal;
  /** Auth endpoints must never trigger the refresh interceptor: it would recurse. */
  skipRefresh?: boolean;
  headers?: Record<string, string>;
  /** `blob` for binary responses — a private document has no readable JSON body. */
  responseType?: 'json' | 'blob';
}

const JSON_TYPE = 'application/json';

export class ApiClient {
  private readonly baseUrl: string;
  private readonly transport: Transport;
  private readonly doFetch: typeof globalThis.fetch;

  store: TokenStore;

  /**
   * Fired once when the session is unrecoverable. Assignable because the store
   * that wants to know needs Pinia, which needs the app, which is created after
   * this client.
   */
  onSessionExpired?: () => void;

  /**
   * The in-flight refresh, if any.
   *
   * Refresh tokens rotate and the server detects reuse: presenting a token that
   * has already been exchanged revokes the whole family and logs the user out
   * everywhere. So when five requests 401 at once, they must not fire five
   * refreshes — four of them would replay a spent token. They all await this one
   * promise instead, then retry with whatever it produced.
   */
  private refreshing: Promise<string | null> | null = null;

  constructor(options: ApiClientOptions = {}) {
    this.baseUrl = options.baseUrl ?? '';
    this.transport = options.transport ?? 'web';
    this.store = options.store ?? memoryTokenStore();
    this.onSessionExpired = options.onSessionExpired;
    this.doFetch = options.fetch ?? globalThis.fetch.bind(globalThis);
  }

  get isMobile(): boolean {
    return this.transport === 'mobile';
  }

  /**
   * Swaps in the keychain-backed store, once, at boot.
   *
   * The exported `api` is constructed at module load, long before the Capacitor plugins can be
   * awaited. Rather than make every importer wait for a client, the native bootstrap hydrates
   * a store and hands it over here — before the router mounts and anything is requested.
   * Any access token adopted in between (there is none today) would be carried across.
   */
  attachStore(store: TokenStore): void {
    const access = this.store.getAccess();
    if (access) store.setAccess(access);
    this.store = store;
  }

  get accessToken(): string | null {
    return this.store.getAccess();
  }

  // ─── Session ───────────────────────────────────────────────────────────────

  /** Records the tokens a login/register/refresh response returned. */
  adoptSession(session: SessionResponse): void {
    this.store.setAccess(session.accessToken);
    if (this.isMobile && session.refreshToken) this.store.setRefresh(session.refreshToken);
    this.store.setSessionHint(true);
  }

  clearSession(): void {
    this.store.clear();
  }

  /** Whether a refresh is even worth attempting. See `TokenStore.hasSessionHint`. */
  get hasSession(): boolean {
    return this.isMobile ? this.store.getRefresh() !== null : this.store.hasSessionHint();
  }

  /**
   * Exchanges the refresh token for a new access token. Single-flight.
   *
   * Resolves to the new access token, or `null` when the session is gone. It never
   * rejects: a failed refresh is an expected outcome (the user was away for a
   * month), and callers all need to distinguish it from a network fault anyway.
   */
  refresh(): Promise<string | null> {
    // `??=` is what makes this single-flight: the second caller through the door
    // gets the first caller's promise, not a second request.
    this.refreshing ??= this.performRefresh().finally(() => {
      this.refreshing = null;
    });
    return this.refreshing;
  }

  private async performRefresh(): Promise<string | null> {
    const refreshToken = this.store.getRefresh();
    if (!this.hasSession) return this.endSession();

    try {
      const session = await this.request<SessionResponse>('/auth/refresh', {
        method: 'POST',
        skipRefresh: true,
        body: this.isMobile ? { refreshToken } : {},
      });
      this.adoptSession(session);
      return session.accessToken;
    } catch (err) {
      // A 5xx or a dropped connection is not proof the session is dead; surfacing
      // it as "logged out" would sign people out every time the API hiccups.
      if (err instanceof ApiError && err.status === 401) return this.endSession();
      throw err;
    }
  }

  private endSession(): null {
    this.clearSession();
    this.onSessionExpired?.();
    return null;
  }

  // ─── Requests ──────────────────────────────────────────────────────────────

  async request<T>(path: string, options: RequestOptions = {}): Promise<T> {
    const response = await this.send(path, options);

    // An anonymous visitor touching a protected route gets a plain 401, not a
    // refresh attempt and a "your session expired" redirect they never had.
    if (response.status !== 401 || options.skipRefresh || !this.hasSession) {
      return this.parse<T>(response, options.responseType);
    }

    // One retry, and only after the shared refresh settles. There is deliberately
    // no second attempt: if the fresh token also 401s, the problem is not the token.
    const token = await this.refresh();
    if (!token) return this.parse<T>(response, options.responseType); // throws the original 401

    return this.parse<T>(await this.send(path, options), options.responseType);
  }

  private async send(path: string, options: RequestOptions): Promise<Response> {
    const headers: Record<string, string> = { ...options.headers };
    if (this.isMobile) headers['X-Client'] = 'mobile';

    const access = this.store.getAccess();
    if (access) headers['Authorization'] = `Bearer ${access}`;

    let body: string | FormData | undefined;
    if (options.body instanceof FormData) {
      // Deliberately no Content-Type: `fetch` writes `multipart/form-data` *and* the
      // boundary it generated. Setting the header ourselves omits the boundary, and the
      // server parses an empty body — a silently truncated upload rather than an error.
      body = options.body;
    } else if (options.body !== undefined) {
      headers['Content-Type'] = JSON_TYPE;
      body = JSON.stringify(options.body);
    }

    return this.doFetch(this.url(path, options.query), {
      method: options.method ?? 'GET',
      headers,
      body,
      signal: options.signal,
      // Sends and accepts the httpOnly refresh cookie on the web transport.
      credentials: this.isMobile ? 'omit' : 'include',
    });
  }

  private url(path: string, query?: RequestOptions['query']): string {
    const search = new URLSearchParams();
    for (const [key, value] of Object.entries(query ?? {})) {
      if (value !== undefined && value !== null && value !== '') search.set(key, String(value));
    }
    const qs = search.toString();
    return `${this.baseUrl}/api/v1${path}${qs ? `?${qs}` : ''}`;
  }

  private async parse<T>(response: Response, responseType: 'json' | 'blob' = 'json'): Promise<T> {
    if (response.status === 204) return undefined as T;

    // Only a *successful* response honours `responseType`. A failure is always the JSON
    // error envelope, whatever the caller hoped the success case would be — reading it as a
    // blob would turn a 403 into an unreadable download.
    if (response.ok && responseType === 'blob') return (await response.blob()) as T;

    const text = await response.text();
    const payload: unknown = text ? safeJson(text) : undefined;

    if (!response.ok) {
      const error = (payload as ApiErrorBody | undefined)?.error;
      throw new ApiError(
        response.status,
        error?.code ?? 'INTERNAL',
        error?.message ?? response.statusText ?? 'Request failed',
        error?.details,
      );
    }

    return payload as T;
  }

  // ─── Sugar ─────────────────────────────────────────────────────────────────

  get<T>(path: string, options: Omit<RequestOptions, 'method' | 'body'> = {}): Promise<T> {
    return this.request<T>(path, { ...options, method: 'GET' });
  }

  post<T>(path: string, body?: unknown, options: RequestOptions = {}): Promise<T> {
    return this.request<T>(path, { ...options, method: 'POST', body: body ?? {} });
  }

  patch<T>(path: string, body: unknown, options: RequestOptions = {}): Promise<T> {
    return this.request<T>(path, { ...options, method: 'PATCH', body });
  }

  /** Wholesale replacement. The availability week is edited and saved as one thing. */
  put<T>(path: string, body: unknown, options: RequestOptions = {}): Promise<T> {
    return this.request<T>(path, { ...options, method: 'PUT', body });
  }

  delete<T>(path: string, options: RequestOptions = {}): Promise<T> {
    return this.request<T>(path, { ...options, method: 'DELETE' });
  }

  /**
   * Fetches bytes through the same auth-and-refresh path as everything else.
   *
   * A private document's `downloadUrl` cannot simply be put in an `<a href>`: on the web
   * transport the access token lives in memory and travels in an `Authorization` header, so
   * the browser's own navigation would arrive unauthenticated and be refused.
   */
  blob(path: string, options: Omit<RequestOptions, 'method' | 'body' | 'responseType'> = {}): Promise<Blob> {
    return this.request<Blob>(path, { ...options, method: 'GET', responseType: 'blob' });
  }
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    // A proxy or gateway can answer with HTML. Do not let that surface as a
    // "JSON.parse" stack trace three layers up.
    return undefined;
  }
}

/** The app-wide client. Tests construct their own. */
export const api = new ApiClient({
  baseUrl: import.meta.env?.VITE_API_URL ?? '',
  transport: import.meta.env?.VITE_CLIENT === 'mobile' ? 'mobile' : 'web',
});
